import { supabase, run } from './db';
import { cached } from './local/cache';
import { queueable } from './local/queued';

/**
 * Laboratórios, membros e perfis.
 *
 * Estes três andam sempre juntos: o acesso do usuário é a interseção deles, e
 * as políticas de RLS entre `lab_members` e `laboratories` são recursivas —
 * por isso o app monta a combinação em passos separados em vez de um join.
 */

export const fetchProfile = (userId) =>
  cached(`profile:${userId}`, async () => {
    const { data, error } = await supabase
      .from('profiles').select('*').eq('id', userId).single();
    // PGRST116 = nenhuma linha. Perfil ausente é estado válido em conta nova.
    if (error && error.code !== 'PGRST116') throw error;
    return data;
  });

export const fetchProfileByEmail = (email, columns = '*') =>
  run(supabase.from('profiles').select(columns).eq('email', email).maybeSingle());

/**
 * Laboratório ativo do perfil.
 *
 * Enfileirável porque trocar de laboratório é navegação, não administração:
 * travar isso offline deixaria o técnico preso no laboratório errado com a
 * ficha na mão.
 */
export const setActiveLaboratory = queueable('perfil.laboratorio-ativo',
  (userId, laboratoryId) =>
    run(supabase.from('profiles').update({ active_laboratory_id: laboratoryId }).eq('id', userId)));

export const fetchActiveMemberships = (userId) =>
  cached(`memberships:${userId}`, () => run(supabase
    .from('lab_members')
    .select('*')
    .eq('user_id', userId)
    .eq('status', 'active')));

export const fetchActiveLaboratories = (labIds) =>
  cached(`laboratories:${[...labIds].sort().join(',')}`, () => run(supabase
    .from('laboratories')
    .select('*')
    .in('id', labIds)
    .eq('status', 'active')));

/**
 * Todos os laboratórios ativos — caminho do super admin.
 *
 * Não passa por `lab_members` de propósito: quem tem esse acesso não é membro
 * de laboratório nenhum, então a consulta por associação voltaria vazia. Quem
 * autoriza é a RLS (policy "super admin ve tudo"); para qualquer outro perfil
 * esta mesma consulta devolve só os labs de que ele já participa.
 */
export const listAllLaboratories = () =>
  cached('laboratorios:todos', () => run(supabase
    .from('laboratories')
    .select('*')
    .eq('status', 'active')
    .order('name')));

export const createLaboratory = (row) =>
  run(supabase.from('laboratories').insert([row]).select().single());

export const addLabMember = (row) =>
  run(supabase.from('lab_members').insert([row]));

/** Laboratorios ativos onde o usuario e membro, ja com o papel dele. */
export const listMyLaboratories = (userId) =>
  run(supabase
    .from('laboratories')
    .select('*, lab_members!inner(role)')
    .eq('lab_members.user_id', userId)
    .eq('status', 'active'));

export const archiveLaboratory = (labId) =>
  run(supabase.from('laboratories').update({ status: 'archived' }).eq('id', labId));

export const deleteLaboratory = (labId) =>
  run(supabase.from('laboratories').delete().eq('id', labId));

/**
 * Remove os membros de um laboratorio.
 *
 * `lab_members` tem ON DELETE CASCADE, mas o app apaga antes de proposito:
 * assim a exclusao do laboratorio falha de forma limpa se algo tiver sido
 * criado nesse meio-tempo, em vez de arrastar junto.
 */
export const deleteLabMembers = (labId) =>
  run(supabase.from('lab_members').delete().eq('laboratory_id', labId));

export const listLabMembers = (labId) =>
  run(supabase.from('lab_members').select('*').eq('laboratory_id', labId));

export const removeLabMember = (memberId) =>
  run(supabase.from('lab_members').delete().eq('id', memberId));

/**
 * Muda funcao e/ou situacao de um membro.
 *
 * Vai por RPC porque o update direto em `lab_members` foi revogado: enquanto
 * existia, qualquer membro escrevia na propria linha e se promovia a admin. A
 * funcao no banco e' que confere quem chama, protege o dono do laboratorio e
 * impede o lab de ficar sem nenhum admin ativo.
 *
 * Nulo em qualquer um dos dois significa "nao mexe nesse".
 */
export const atualizarMembro = (memberId, { role = null, status = null } = {}) =>
  run(supabase.rpc('atualizar_membro', {
    p_member_id: memberId, p_role: role, p_status: status,
  }));

/**
 * Nome e email dos membros de um laboratório.
 *
 * Vai por função no banco, não por SELECT em `profiles`: a policy de leitura de
 * lá é `auth.uid() = id`, então cada um só vê o próprio perfil — a consulta
 * direta voltava vazia e a tela mostrava o id no lugar do nome.
 *
 * A função devolve só as três colunas que a tela usa, e confere lá dentro se
 * quem pergunta é do mesmo laboratório.
 */
export const fetchLabMemberProfiles = async (laboratoryId) => {
  try {
    return await cached(`perfis-membros:${laboratoryId}`,
      () => run(supabase.rpc('lab_member_profiles', { p_laboratory_id: laboratoryId })));
  } catch (err) {
    // Tolerante: a lista de membros ainda serve sem os nomes.
    console.error('Erro ao carregar perfis dos membros:', err);
    return [];
  }
};

/**
 * Quantas linhas de cada tabela estão vinculadas ao laboratório.
 *
 * É o que decide entre arquivar (dados preservados) e excluir de vez. Usa
 * `head: true`, então traz só a contagem, sem puxar as linhas.
 */
export const countLabContents = async (labId, tables = ['clients', 'services', 'animals', 'embryos']) => {
  const results = await Promise.all(tables.map(t =>
    supabase.from(t).select('id', { count: 'exact', head: true }).eq('laboratory_id', labId)
  ));
  const counts = {};
  tables.forEach((t, i) => { counts[t] = results[i].count || 0; });
  // `total` sai daqui junto porque quem chama só quer saber se há algo a perder.
  counts.total = tables.reduce((sum, t) => sum + counts[t], 0);
  return counts;
};
